import { useState, useEffect, useContext, useCallback } from "react"
import { Plus, RefreshCw, Trash2, ExternalLink, Plug } from "lucide-react"

// Firebase utilities
import { db } from "../../../../utils/firebase"
import { AlertsContext } from "../../../../utils/providers/alerts"

import AddConnectorModal from "./add-connector-modal"

import "./connectors.scss"

const STATUS_LABEL = {
	active: "Active",
	testing: "Testing…",
	failed: "Failed",
}

const TIER_LABEL = {
	API: "Direct API",
	BROWSER: "Headless browser",
	AGENT: "AI browser agent",
}

// Firestore Timestamp → "8 Jun 2026, 14:32", or "—" when unset.
const formatChecked = (ts) => {
	const date = ts?.toDate ? ts.toDate() : null
	if (!date) return "never"
	return date.toLocaleString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	})
}

const seconds = (ts) => (ts?.seconds ? ts.seconds : 0)

/**
 * Connectors tab — shared by courses and holidays. Lists the saved availability
 * connectors on the entity (live via onSnapshot) as status cards with retest +
 * delete, and opens the add-connector modal to create or re-run one.
 */
export default function Connectors({ entityKind, entityId }) {
	const isCourse = entityKind === "CMS_COURSE"
	const collectionPath = isCourse ? `CMS_courses/${entityId}` : `CMS_holidays/${entityId}`
	const { pushAlert } = useContext(AlertsContext)

	const [connectors, setConnectors] = useState([])
	const [loading, setLoading] = useState(true)
	const [listError, setListError] = useState(null)
	const [modalOpen, setModalOpen] = useState(false)
	const [editing, setEditing] = useState(null)

	// Live subscription to the connectors subcollection. The error handler is
	// important: until firestore.rules is deployed the connectors subcollection
	// read is denied, which would otherwise leave `loading` stuck true and the
	// whole tab blank.
	useEffect(() => {
		setLoading(true)
		const unsubscribe = db.collection(`${collectionPath}/connectors`).onSnapshot(
			(snap) => {
				const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
				rows.sort((a, b) => seconds(b.createdAt) - seconds(a.createdAt))
				setConnectors(rows)
				setListError(null)
				setLoading(false)
			},
			(error) => {
				setListError(error)
				setLoading(false)
			}
		)
		return () => unsubscribe()
	}, [collectionPath])

	const openAdd = () => {
		setEditing(null)
		setModalOpen(true)
	}

	const openRetest = (connector) => {
		setEditing(connector)
		setModalOpen(true)
	}

	const remove = useCallback(
		async (connector) => {
			// eslint-disable-next-line no-alert
			if (!window.confirm(`Delete the connector for ${connector.label || connector.url}?`)) return
			try {
				await db.doc(`${collectionPath}/connectors/${connector.id}`).delete()
			} catch (error) {
				pushAlert({ type: "ERROR", title: "Could not delete connector", body: error.message })
			}
		},
		[collectionPath, pushAlert]
	)

	const entityName = isCourse ? "course" : "holiday"

	return (
		<div className="entity-connectors">
			<div className="ec-head">
				<div>
					<h3>Connectors</h3>
					<p>Drop a live tee sheet in and the system will work out how to read it.</p>
				</div>
				<button
					type="button"
					className="connector-button connector-button--primary"
					onClick={openAdd}>
					<Plus size={15} />
					Add connector
				</button>
			</div>

			{listError && (
				<div className="ec-empty">
					<p>
						Couldn't load connectors for this {entityName}. If the feature has just been deployed, the
						connectors Firestore rules may not be live yet ({listError.code || "permission-denied"}).
					</p>
				</div>
			)}

			{!loading && !listError && connectors.length === 0 && (
				<div className="ec-empty">
					<p>No connectors yet for this {entityName}.</p>
					<button
						type="button"
						className="connector-button connector-button--primary"
						onClick={openAdd}>
						<Plus size={15} />
						Add connector
					</button>
				</div>
			)}

			<div className="ec-list">
				{connectors.map((connector) => {
					const status = connector.status || "testing"
					const tier = connector.method?.tier
					return (
						<div
							className="ec-card"
							key={connector.id}>
							<div className="ec-card-icon">
								<Plug size={17} />
							</div>
							<div className="ec-card-body">
								<div className="ec-card-title">
									<span>{connector.label || connector.url}</span>
									<span className={`ec-status ec-status--${status}`}>{STATUS_LABEL[status] || status}</span>
								</div>
								<a
									className="ec-card-url"
									href={connector.url}
									target="_blank"
									rel="noreferrer">
									{connector.url}
								</a>
								<div className="ec-card-meta">
									{tier ? `${TIER_LABEL[tier] || tier} · ` : ""}
									Last checked {formatChecked(connector.lastCheckedAt)}
								</div>
							</div>
							<div className="ec-card-actions">
								<a
									className="ec-icon-button"
									href={connector.url}
									target="_blank"
									rel="noreferrer"
									title="Open tee-sheet">
									<ExternalLink size={16} />
								</a>
								<button
									type="button"
									className="ec-icon-button"
									onClick={() => openRetest(connector)}
									title="Retest connector">
									<RefreshCw size={16} />
								</button>
								<button
									type="button"
									className="ec-icon-button is-danger"
									onClick={() => remove(connector)}
									title="Delete connector">
									<Trash2 size={16} />
								</button>
							</div>
						</div>
					)
				})}
			</div>

			{modalOpen && (
				<AddConnectorModal
					entityKind={entityKind}
					entityId={entityId}
					existingConnector={editing}
					onClose={() => setModalOpen(false)}
				/>
			)}
		</div>
	)
}
